/**
 * Oath Payment Plan Service
 * API calls for the agent-facing payment plan section (Power Reporting -> Batches to Pay).
 * Agents view their agency's active plans and pay them down by ACH.
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response - extracts data from various response formats
 */
const parseResponse = async (response) => {
  const json = await response.json().catch(() => ({}));
  return json;
};

// ============================================
// PAYMENT PLAN ENDPOINTS
// ============================================

/**
 * Get the agency's active payment plans with balances and payment history.
 *
 * Used for the plan detail panel expanded from a PaymentPlan batch row — paying a plan goes
 * through the ordinary batch checkout, not this service.
 *
 * A 403 here is normal and expected — it means the user lacks READ:AR (the slug
 * OathPaymentPlanController gates on), so callers should treat it as "no plans to show"
 * rather than an error to surface.
 * @param {number} agencyId - Agency ID
 * @returns {Promise<{success: boolean, data: PaymentPlan[], forbidden?: boolean, error?: string}>}
 */
export const getPaymentPlans = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/agency/${agencyId}/payment-plans`);
    const result = await parseResponse(response);

    if (response.status === 403) {
      return { success: false, forbidden: true, data: [] };
    }

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to load payment plans', data: [] };
    }

    return {
      success: result.success !== false,
      data: result.data || [],
    };
  } catch (error) {
    console.error('Get payment plans error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

// NOTE: there is deliberately no payPaymentPlan here any more. A plan is collected as a
// real batch in Batches to Pay, so the agent pays it through payBatches() like anything
// else and the server records the plan payment. The old direct-ACH endpoint still exists
// (marked [Obsolete]) purely so payments already taken through it stay voidable.

// ============================================
// HELPERS
// ============================================

/**
 * How the plan's collection terms read on one line, e.g.
 * "$1,000.00 monthly, due the 15th". Returns null when the plan has no
 * monthly amount recorded (older plans predate the field).
 * @param {object} plan
 * @param {(amount: number) => string} formatCurrency
 * @returns {string|null}
 */
export const formatPlanTerms = (plan, formatCurrency) => {
  if (!plan?.monthlyPaymentAmount) return null;

  const cadence = (plan.collectionFrequency || 'Monthly').toLowerCase();
  const amount = formatCurrency(plan.monthlyPaymentAmount);

  if (!plan.paymentDueDay) return `${amount} ${cadence}`;
  return `${amount} ${cadence}, due the ${ordinal(plan.paymentDueDay)}`;
};

// NOTE: there is deliberately no source-code lookup table here. `payments[].method` is
// ALREADY agent-facing display text — OathPaymentPlanRepository collapses the raw
// PaymentPlanTransactionSource codes to "Paid online" / "Received by Bankers" on purpose,
// so agents never see internal codes or who at Bankers keyed a payment. An earlier version
// of this file mapped the raw codes (Check/ERReport/ManualJE/PortalACH), which could never
// match what the API actually sends. Render `method` directly; fall back to an em dash.

/**
 * 1 -> "1st", 2 -> "2nd", 15 -> "15th".
 * @param {number} n
 * @returns {string}
 */
export const ordinal = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);

  const mod100 = num % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${num}th`;

  switch (num % 10) {
    case 1: return `${num}st`;
    case 2: return `${num}nd`;
    case 3: return `${num}rd`;
    default: return `${num}th`;
  }
};
